import React, { useCallback, useMemo, useState } from "react";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { ChartWrapper, GroupedBarChart, HorizontalBarChart, LineChart } from "components/charts";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faClockNine, faEnvelope, type IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { faCircle } from "@fortawesome/pro-regular-svg-icons";
import { sharedAccountReportingHorizontalBarProps } from "features/Reporting/Content/shared";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { type ColumnOption } from "interfaces/columnOptions";
import {
  noBarData,
  noData,
  createDateRange,
  lineChartMargins,
  getFormattedTimeStringFromPeriod,
  formatUserName,
  getPriorityCell,
  isUserDeleted,
  canUseChildAccount,
  type PerformanceWithAccountFilter,
  validLineData,
  isDateFilterValid,
} from "../../../Common/utils/performanceUtils";
import { EmptyStateWrapper } from "../EmptyStateWrapper";
import { useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import type SortDirections from "enums/sortDirections";
import dateTimeUtils from "utils/dateTimeUtils";
import "../flowPerformance.scss";
import {
  useFlowPriorityLineChartQuery,
  useFlowPriorityBarChartQuery,
  useFlowPriorityCardsQuery,
  useFlowPriorityPeopleDetailsQuery,
  colorRange,
  useFlowPriorityFlowDetailsQuery,
  type FlowPriorityPeopleData,
  type FlowPriorityFlowData,
  generatePrioritizedFlowStatistics,
} from "./FlowPrioritizationQueries";
import styles from "./FlowPrioritization.module.scss";
import { useGoogleTooltipFormats } from "features/Reporting/Google/utils/useGoogleTooltipFormats";
import { Button } from "components/buttons/button/Button";
import NotifyUsersModal from "features/Library/Flows/Edit/NotifyUsersModal/NotifyUsersModal";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "redux";
import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { sendTransientNotification as sendTransientNotificationAction } from "features/Notifications/state/notificationsActions";
import type { FlowPriorityModal } from "../../types/performance";

interface FlowPriorityPeopleTableProps {
  data: FlowPriorityPeopleData[];
  filter: PerformanceWithAccountFilter;
  accountId: number;
  onSort?: (columnName: string, dir: SortDirections) => void;
}

interface FlowPriorityFlowTableProps {
  data: FlowPriorityFlowData[];
  filter: PerformanceWithAccountFilter;
  includeAccounts?: boolean;
  childAccountId?: number;
  onSort?: (columnName: string, dir: SortDirections) => void;
  onNotify: () => void;
  onSelectionChanged: (data: FlowPriorityFlowData[]) => void;
  hasCreateAccountsAddOn?: boolean;
}

enum StatusIcons {
  NotStarted = "NotStarted",
  InProgress = "InProgress",
  Completed = "Completed",
}

const StatusToIcon: { [key in StatusIcons]: IconDefinition } = {
  NotStarted: faCircle,
  InProgress: faClockNine,
  Completed: faCircleCheck,
};

const StatusToLabel: { [key in StatusIcons]: string } = {
  NotStarted: "Not Started",
  InProgress: "Started",
  Completed: "Completed",
};

const LINE_CHART_TITLES = ["Prioritized User Activity - Line Chart", "Prioritized User Activity - Bar Chart"];

const FlowPriorityPeopleTable = ({ data, filter, accountId }: FlowPriorityPeopleTableProps) => {
  const columnOptions: ColumnOption<FlowPriorityPeopleData>[] = useMemo(
    () => [
      {
        name: "NAME",
        width: 5,
        render: (entity: FlowPriorityPeopleData) => {
          const formattedNameDisplay = formatUserName(entity.FirstName, entity.LastName);
          if (isUserDeleted(entity)) {
            return (
              <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
            );
          }
          return (
            <MoboLink
              target={entity.AccountId !== accountId ? "_blank" : undefined}
              accountId={entity.AccountId !== accountId ? entity.AccountId : undefined}
              to={`/${RouteNames.peopleUsers}/${entity.UserId}`}
              className={styles.linkButton}
            >
              <Tooltip target={<TextTruncate>{formattedNameDisplay}</TextTruncate>} content={formattedNameDisplay} />
            </MoboLink>
          );
        },
      },
      {
        name: "EMAIL",
        width: 5,
        render: (entity: FlowPriorityPeopleData) => (
          <div>
            <Tooltip target={<TextTruncate>{entity.Email}</TextTruncate>} content={entity.Email} />
          </div>
        ),
      },
      {
        name: "PRIORITY",
        width: 3,
        render: (entity: FlowPriorityPeopleData) => {
          return getPriorityCell(entity.Priority);
        },
      },
      {
        name: "STATUS",
        width: 3,
        render: (entity: FlowPriorityPeopleData) => {
          const statusConfig = StatusToIcon[entity.Status];
          return (
            <div className={styles.icon}>
              <FontAwesomeIcon icon={statusConfig} className={styles[`${statusConfig}`]} />
              {StatusToLabel[entity.Status]}
            </div>
          );
        },
      },
      {
        name: "LAST ACTIVITY",
        width: 4,
        render: (entity: FlowPriorityPeopleData) => {
          const date = entity.LastActivity ? dateTimeUtils.formatDate(entity.LastActivity) : "-";
          return (
            <div>
              <div>{date}</div>
            </div>
          );
        },
      },
    ],
    [accountId],
  );

  return <GenericPerformanceList rows={data} columns={columnOptions} filter={filter} title="People" />;
};

const FlowPriorityFlowTable = ({
  data,
  filter,
  onNotify,
  onSelectionChanged,
  childAccountId,
  includeAccounts,
  hasCreateAccountsAddOn,
}: FlowPriorityFlowTableProps) => {
  const columnOptions: ColumnOption<FlowPriorityFlowData>[] = useMemo(
    () => [
      {
        name: "FLOW NAME",
        width: 6,
        render: (entity: FlowPriorityFlowData) => {
          const target = (
            <TextTruncate lines={2} className={styles.wordBreak}>
              {entity.FlowName}
            </TextTruncate>
          );
          const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
          const shouldShowLink = !(includeAccounts && !childAccountEnabled);
          return (
            <>
              {shouldShowLink ? (
                <Tooltip
                  target={
                    <MoboLink
                      target={childAccountEnabled ? "_blank" : undefined}
                      accountId={childAccountEnabled ? childAccountId : undefined}
                      to={`/${RouteNames.contentFlows}/${entity.FlowId}`}
                      state={{ dateTo: filter.dateTo }}
                    >
                      {target}
                    </MoboLink>
                  }
                  content={entity.FlowName}
                  showAlways
                />
              ) : (
                <Tooltip target={target} content={entity.FlowName} showAlways />
              )}
            </>
          );
        },
      },
      {
        name: "PRIORITY",
        width: 3,
        render: (entity: FlowPriorityFlowData) => {
          return getPriorityCell(entity.FlowPriority);
        },
      },
      {
        name: "TOTAL USERS",
        width: 3,
        render: (entity: FlowPriorityFlowData) => entity.TotalUsers.toLocaleString(),
      },
      {
        name: "NOT STARTED",
        width: 3,
        render: (entity: FlowPriorityFlowData) => entity.NotStarted.toLocaleString(),
      },
      {
        name: "STARTED",
        width: 3,
        render: (entity: FlowPriorityFlowData) => entity.InProgress.toLocaleString(),
      },
      {
        name: "COMPLETED",
        width: 3,
        render: (entity: FlowPriorityFlowData) => entity.Completed.toLocaleString(),
      },
      {
        name: "",
        width: 1,
        render: (entity: FlowPriorityFlowData) => {
          return (
            <NotifyUsersModal
              appliedStatuses={filter.status}
              flowInfos={[{ FlowId: entity.FlowId, FlowPriority: entity.FlowPriority }]}
              onNotifySuccess={onNotify}
              renderTrigger={(onOpen) => (
                <Button
                  icon={faEnvelope}
                  size={"medium"}
                  onClick={onOpen}
                  borderless
                  basic
                  color={"blue"}
                  className={styles.notifyButton}
                />
              )}
            />
          );
        },
      },
    ],
    [filter.dateTo, filter.status, onNotify, childAccountId, includeAccounts, hasCreateAccountsAddOn],
  );

  const mappedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      id: `${item.FlowId}-${item.FlowPriority}`,
    }));
  }, [data]);

  return (
    <GenericPerformanceList
      withSelection
      rows={mappedData}
      columns={columnOptions}
      filter={filter}
      title="Prioritized Flows"
      onSelectionChanged={onSelectionChanged}
    />
  );
};

export interface Props extends PropsFromRedux {
  dateFilter: PerformanceWithAccountFilter;
  includeAccounts?: boolean;
  childAccountId?: number;
  flowId?: number;
  hasCreateAccountsAddOn?: boolean;
}

const FlowPrioritization: React.FC<Props> = ({
  flowId,
  dateFilter,
  sendTransientNotification,
  childAccountId,
  accountId,
  includeAccounts,
  hasCreateAccountsAddOn,
}) => {
  const [chartState, setChartState] = useState("Prioritized User Activity - Line Chart");
  const [selectedFlowIds, setSelectedFlowIds] = useState<FlowPriorityModal[]>([]);
  const dateRange = useMemo(() => {
    return createDateRange(dateFilter?.dateFrom, dateFilter?.dateTo);
  }, [dateFilter]);

  const noCardData = generatePrioritizedFlowStatistics(
    {
      TotalPrioritizedFlows: 0,
      TotalPrioritizedUsers: 0,
      TotalRequiredFlows: 0,
      TotalImportantFlows: 0,
      TotalRecommendedFlows: 0,
    },
    flowId,
  );

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange ?? [],
    lineChartMargins.left + lineChartMargins.right,
  );
  const lineChartQuery = useFlowPriorityLineChartQuery({ ...dateFilter, aggregation: chartPeriod }, flowId);
  const barChartQuery = useFlowPriorityBarChartQuery({ ...dateFilter }, flowId);
  const cardsQuery = useFlowPriorityCardsQuery({ ...dateFilter }, flowId);
  const peopleQuery = useFlowPriorityPeopleDetailsQuery({ ...dateFilter }, flowId);
  const flowQuery = useFlowPriorityFlowDetailsQuery({ ...dateFilter }, flowId);

  const { groupedBarFormatter } = useGoogleTooltipFormats(chartPeriod, dateRange);

  const activityChart = useMemo(() => {
    if (!lineChartQuery.data) return null;
    return chartState === LINE_CHART_TITLES[0] ? (
      <LineChart {...lineChartQuery.data.lineProps} />
    ) : (
      <GroupedBarChart
        {...lineChartQuery.data.barProps}
        tooltipFormatter={groupedBarFormatter}
        xTickFormatter={(d) => getFormattedTimeStringFromPeriod(d.toString(), chartPeriod, dateRange)}
      />
    );
  }, [chartState, lineChartQuery.data, chartPeriod, dateRange, groupedBarFormatter]);

  const activityLegendLabels = ["Incomplete", "Not Started", "Started", "Completed"];

  const handleNotifySuccess = () => {
    sendTransientNotification({ title: "Success!", message: "Email sent successfully.", type: 0 }, 5);
  };

  const handleFlowSelectionChanged = useCallback((data: FlowPriorityFlowData[]) => {
    setSelectedFlowIds(data.map((item) => ({ FlowId: item.FlowId, FlowPriority: item.FlowPriority })));
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.prioritization}>
        <div className={styles.charts}>
          <div ref={measureRef}>
            <ChartWrapper
              titles={LINE_CHART_TITLES}
              showLegend
              legendLabels={activityLegendLabels}
              colorRange={colorRange}
              onChange={setChartState}
            >
              <EmptyStateWrapper dateFilter={dateFilter} emptyState={noData(isDateFilterValid(dateFilter))}>
                <RequestStatusRenderer state={lineChartQuery.status}>
                  {lineChartQuery.isSuccess && validLineData(lineChartQuery.data.lineProps.yData)
                    ? activityChart
                    : noData(dateFilter)}
                </RequestStatusRenderer>
              </EmptyStateWrapper>
            </ChartWrapper>
          </div>
          <ChartWrapper titles={["Prioritized Users by Status"]}>
            <EmptyStateWrapper dateFilter={dateFilter} emptyState={noData(isDateFilterValid(dateFilter))}>
              <RequestStatusRenderer state={barChartQuery.status}>
                {barChartQuery.data?.barData && !noBarData(...barChartQuery.data.barData.map((d) => d.value)) ? (
                  <HorizontalBarChart
                    {...sharedAccountReportingHorizontalBarProps}
                    domain={barChartQuery.data.barDomain}
                    data={barChartQuery.data.barData}
                  />
                ) : (
                  noData(null)
                )}
              </RequestStatusRenderer>
            </EmptyStateWrapper>
          </ChartWrapper>
        </div>
        <div className={styles.cards}>
          <EmptyStateWrapper dateFilter={dateFilter} emptyState={<CardReporting items={noCardData} />}>
            <RequestStatusRenderer state={cardsQuery.status}>
              <CardReporting items={cardsQuery.data!} />
            </RequestStatusRenderer>
          </EmptyStateWrapper>
        </div>
      </div>
      <div className="performanceTableSection">
        <div className={styles.selectionHeader}>
          {selectedFlowIds.length > 0 && (
            <>
              <p className={styles.selectedCount}>{selectedFlowIds.length} Selected</p>
              <NotifyUsersModal
                appliedStatuses={dateFilter.status}
                flowInfos={selectedFlowIds}
                onNotifySuccess={handleNotifySuccess}
                renderTrigger={(onOpen) => (
                  <Button
                    icon={faEnvelope}
                    size={"medium"}
                    basic
                    borderless
                    className={styles.notifyButton}
                    color={"blue"}
                    onClick={onOpen}
                  >
                    Notify Users
                  </Button>
                )}
              />
            </>
          )}
        </div>
        {flowId ? (
          <EmptyStateWrapper dateFilter={dateFilter} emptyState={noData(isDateFilterValid(dateFilter))}>
            <RequestStatusRenderer state={peopleQuery.status}>
              <FlowPriorityPeopleTable accountId={accountId} data={peopleQuery.data!} filter={dateFilter} />
            </RequestStatusRenderer>
          </EmptyStateWrapper>
        ) : (
          <RequestStatusRenderer state={flowQuery.status}>
            <FlowPriorityFlowTable
              includeAccounts={includeAccounts}
              data={flowQuery.data!}
              filter={dateFilter}
              onNotify={handleNotifySuccess}
              onSelectionChanged={handleFlowSelectionChanged}
              childAccountId={childAccountId}
              hasCreateAccountsAddOn={hasCreateAccountsAddOn}
            />
          </RequestStatusRenderer>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  sendTransientNotification: bindActionCreators(sendTransientNotificationAction, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(FlowPrioritization);
export default ConnectedComponent;
